/** Accessible desktop disclosures and a modal mobile navigation sheet. */
export class RelaunchHeader {
  constructor(scope) {
    this.scope = scope;
    this.doc = scope.ownerDocument;
    this.win = this.doc.defaultView;
    this.listeners = [];
    this.desktop = this.win.matchMedia(this.doc.documentElement.lang.startsWith('fr') ? '(min-width:1400px)' : '(min-width:1200px)');
    this.buttons = [...scope.querySelectorAll('[data-menu-toggle]')];
    this.mobileButtons = [...scope.querySelectorAll('[data-mobile-toggle]')];
    this.drawerButton = scope.querySelector('[data-drawer-toggle]');
    this.drawer = scope.querySelector('#amt-mobile-menu');
    this.drawerClose = scope.querySelector('[data-drawer-close]');
    this.drawerSheet = scope.querySelector('.amt-drawer__sheet');
    this.inertElements = [];
    this.openButton = null;
    // Native buttons activate with click, Enter or Space. Do not open on focus
    // or hover: tabbing through the main navigation must skip closed submenus.
    this.buttons.forEach((button) => {
      this.on(button, 'click', () => this.open(this.openButton === button ? null : button));
    });
    const all = scope.querySelector('.amt-header__all');
    this.topLevel = [...this.buttons, all].filter(Boolean);
    this.topLevel.forEach((item) => this.on(item, 'keydown', (event) => this.navigate(event, item)));
    this.on(scope, 'focusout', (event) => {
      if (!this.drawer.hidden) return;
      if (this.openButton && event.relatedTarget !== this.openButton && !this.panel(this.openButton).contains(event.relatedTarget)) this.open(null);
    });
    this.on(this.doc, 'click', (event) => { if (!scope.contains(event.target)) this.closeAll(true); });
    this.on(this.doc, 'keydown', (event) => {
      if (!this.drawer.hidden && event.key === 'Tab') { this.trapFocus(event); return; }
      if (event.key !== 'Escape' || (!this.openButton && this.drawer.hidden)) return;
      event.preventDefault();
      const target = this.openButton;
      this.closeAll(true); target?.focus();
    });
    this.on(this.doc, 'focusin', (event) => {
      if (!this.drawer.hidden && !this.drawer.contains(event.target)) this.drawerClose.focus({ preventScroll: true });
    });
    this.on(this.drawerButton, 'click', () => this.setDrawer(this.drawer.hidden));
    this.on(this.drawerClose, 'click', () => this.setDrawer(false, true));
    this.on(scope.querySelector('[data-drawer-backdrop]'), 'click', () => this.setDrawer(false, true));
    this.mobileButtons.forEach((button) => this.on(button, 'click', () => {
      const open = button.getAttribute('aria-expanded') !== 'true';
      this.setMobile(button, open);
    }));
    this.on(scope, 'click', (event) => {
      const link = event.target.closest('a');
      if (!link || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.hasAttribute('data-menu-search')) {
        const field = this.doc.querySelector('.theme-amt-library [data-search]');
        if (field) {
          // The library also listens for #library clicks on document. Keep the
          // visitor's current search and filters when this shortcut is used.
          event.preventDefault(); event.stopPropagation(); this.closeAll();
          field.scrollIntoView({ block: 'center', behavior: this.motion() });
          field.focus({ preventScroll: true }); return;
        }
        // A distinct, content-free hash lets the destination focus the search
        // field without putting a visitor's query in the URL or session storage.
        const destination = new URL(link.href, this.win.location.href);
        destination.hash = 'library-search';
        event.preventDefault(); this.closeAll();
        this.win.location.assign(destination.href);
        return;
      }
      this.closeAll();
    });
    this.on(this.desktop, 'change', () => this.closeAll(true));
    this.on(this.win, 'resize', () => this.measure());
    this.on(this.win, 'scroll', () => this.measure(), { passive: true });
    this.on(this.win.visualViewport, 'resize', () => this.measure());
    this.on(this.win.visualViewport, 'scroll', () => this.measure());
    this.measure();
  }
  motion() { return this.win.matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth'; }
  on(target, event, handler, options) {
    if (!target) return;
    target.addEventListener(event, handler, options);
    this.listeners.push(() => target.removeEventListener(event, handler, options));
  }
  panel(button) { return this.doc.getElementById(button.getAttribute('aria-controls')); }
  navigate(event, item) {
    if (!this.desktop.matches || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    if (['ArrowDown', 'ArrowUp'].includes(event.key) && this.buttons.includes(item)) {
      event.preventDefault();
      this.open(item);
      const links = [...this.panel(item).querySelectorAll('a[href]')];
      (event.key === 'ArrowUp' ? links[links.length - 1] : links[0])?.focus();
      return;
    }
    const index = this.topLevel.indexOf(item);
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % this.topLevel.length;
    else if (event.key === 'ArrowLeft') next = (index + this.topLevel.length - 1) % this.topLevel.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.topLevel.length - 1;
    else return;
    event.preventDefault();
    this.open(null);
    this.topLevel[next].focus();
  }
  measure() {
    const available = Math.max(120, this.win.innerHeight - this.scope.getBoundingClientRect().bottom - 8);
    this.scope.style.setProperty('--amt-menu-available', `${available}px`);
    const viewport = this.win.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const toolbarBottom = this.doc.getElementById('wpadminbar')?.getBoundingClientRect().bottom || 0;
    const top = Math.max(viewportTop, toolbarBottom);
    this.scope.style.setProperty('--amt-drawer-top', `${top}px`);
    this.scope.style.setProperty('--amt-drawer-height', `${Math.max(0, (viewport?.height || this.win.innerHeight) + viewportTop - top)}px`);
  }
  open(button) {
    if (button && !this.desktop.matches) return;
    this.buttons.forEach((item) => {
      const active = item === button;
      item.setAttribute('aria-expanded', String(active)); this.panel(item).hidden = !active;
    });
    this.openButton = button;
    this.measure();
  }
  setMobile(button, open) {
    if (open) {
      this.mobileButtons.forEach((item) => {
        if (item !== button && item.dataset.mobileLevel === button.dataset.mobileLevel) this.setMobile(item, false);
      });
    }
    button.setAttribute('aria-expanded', String(open));
    const panel = this.panel(button);
    panel.hidden = !open;
    if (!open) this.mobileButtons.filter((item) => panel.contains(item)).forEach((item) => this.setMobile(item, false));
  }
  setDrawer(open, restoreFocus = false) {
    if (open && this.desktop.matches) return;
    const wasOpen = !this.drawer.hidden;
    if (open === wasOpen) return;
    if (open) {
      this.open(null);
      // Touch activation does not focus buttons in every browser.
      this.returnFocus = this.drawerButton;
      const region = this.scope.querySelector('[data-region-selector]');
      if (region) region.open = false;
      this.lockPage();
    }
    this.drawer.hidden = !open;
    this.drawerButton.setAttribute('aria-expanded', String(open));
    this.drawerButton.querySelector('[data-drawer-label]').textContent = open ? this.drawerButton.dataset.closeLabel : this.drawerButton.dataset.openLabel;
    if (open) {
      this.measure();
      this.drawer.querySelector('.amt-drawer__body').scrollTop = 0;
      this.drawerClose.focus({ preventScroll: true });
    } else {
      this.mobileButtons.forEach((button) => this.setMobile(button, false));
      this.unlockPage();
      if (restoreFocus) {
        const target = this.returnFocus?.isConnected && this.returnFocus.getClientRects().length
          ? this.returnFocus : this.desktop.matches ? this.scope.querySelector('.amt-header__logo') : this.drawerButton;
        target?.focus({ preventScroll: true });
      }
      this.returnFocus = null;
      this.measure();
    }
  }
  trapFocus(event) {
    const targets = [...this.drawerSheet.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])')]
      .filter((target) => target.getClientRects().length && !target.closest('[inert]'));
    const first = targets[0] || this.drawer;
    const last = targets[targets.length - 1] || this.drawer;
    const active = this.doc.activeElement;
    if ((event.shiftKey && (active === first || !targets.includes(active))) || (!event.shiftKey && (active === last || !targets.includes(active)))) {
      event.preventDefault();
      (event.shiftKey ? last : first).focus({ preventScroll: true });
    }
  }
  lockPage() {
    this.scrollPosition = { x: this.win.scrollX, y: this.win.scrollY };
    this.savedStyles = [];
    const set = (element, property, value) => {
      this.savedStyles.push([element, property, element.style.getPropertyValue(property), element.style.getPropertyPriority(property)]);
      element.style.setProperty(property, value);
    };
    const scrollbar = Math.max(0, this.win.innerWidth - this.doc.documentElement.clientWidth);
    if (scrollbar) set(this.doc.body, 'padding-right', `${parseFloat(this.win.getComputedStyle(this.doc.body).paddingRight) + scrollbar}px`);
    set(this.doc.documentElement, 'overflow', 'hidden');
    set(this.doc.body, 'position', 'fixed');
    set(this.doc.body, 'top', `${-this.scrollPosition.y}px`);
    set(this.doc.body, 'left', `${-this.scrollPosition.x}px`);
    set(this.doc.body, 'width', '100%');
    set(this.doc.body, 'overflow', 'hidden');
    // Inert each sibling on the route to body, including the closed header bar
    // and the WordPress toolbar, without hiding an ancestor of the dialog.
    let current = this.drawer;
    while (current && current !== this.doc.body) {
      for (const sibling of current.parentElement?.children || []) {
        if (sibling === current) continue;
        this.inertElements.push([sibling, sibling.inert]);
        sibling.inert = true;
      }
      current = current.parentElement;
    }
  }
  unlockPage() {
    this.inertElements.forEach(([element, inert]) => { element.inert = inert; });
    this.inertElements = [];
    this.savedStyles?.forEach(([element, property, value, priority]) => {
      if (value) element.style.setProperty(property, value, priority);
      else element.style.removeProperty(property);
    });
    this.savedStyles = null;
    if (this.scrollPosition) {
      const style = this.doc.documentElement.style;
      const behavior = style.getPropertyValue('scroll-behavior');
      const priority = style.getPropertyPriority('scroll-behavior');
      style.setProperty('scroll-behavior', 'auto');
      this.win.scrollTo(this.scrollPosition.x, this.scrollPosition.y);
      if (behavior) style.setProperty('scroll-behavior', behavior, priority);
      else style.removeProperty('scroll-behavior');
      this.scrollPosition = null;
    }
  }
  closeAll(restoreFocus = false) { this.open(null); this.setDrawer(false, restoreFocus); }
  destroy() {
    this.closeAll(true); this.listeners.forEach((off) => off());
    ['--amt-menu-available', '--amt-drawer-top', '--amt-drawer-height'].forEach((property) => this.scope.style.removeProperty(property));
  }
}
